import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import type { MDWriteOffTrendPoint } from '../../api/dashboard.api';

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; dataKey: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white rounded-lg p-3 text-xs shadow-lg">
      <p className="font-semibold text-sm mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} className="mt-0.5">
          {p.name}: {p.value}%
        </p>
      ))}
    </div>
  );
}

interface WriteOffChartProps {
  data: MDWriteOffTrendPoint[];
}

export function WriteOffChart({ data }: WriteOffChartProps) {
  const { t } = useTranslation();
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
        <YAxis
          tickFormatter={v => `${v}%`}
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          axisLine={false}
          tickLine={false}
          tickCount={6}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          align="right"
          verticalAlign="top"
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: 11, color: '#64748b', paddingBottom: 8 }}
        />
        <Line
          type="monotone"
          dataKey="write_off"
          name={t('dashboard.writeOffSeries')}
          stroke="#ef4444"
          strokeWidth={2}
          dot={{ r: 3, fill: '#ef4444', stroke: '#fff', strokeWidth: 2 }}
          activeDot={{ r: 5 }}
        />
        <Line
          type="monotone"
          dataKey="adjustments"
          name={t('dashboard.adjustmentsSeries')}
          stroke="#f59e0b"
          strokeWidth={2}
          dot={{ r: 3, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }}
          activeDot={{ r: 5 }}
        />
        <Line
          type="monotone"
          dataKey="escalations"
          name={t('dashboard.escalationsSeries')}
          stroke="#0069D1"
          strokeWidth={2}
          dot={{ r: 3, fill: '#0069D1', stroke: '#fff', strokeWidth: 2 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
